import type { CustomField } from '@puckeditor/core';

import type { BoxSpacing } from './tokens';

import { NumberField } from './widgets/number-field';

/** JSON schema for a `NumberField('px')`-backed value, e.g. '8px'. */
export const PX_AI_SCHEMA = { type: 'string', pattern: '^\\d+px$' } as const;

const SPACING_SIDE_LABELS = { top: 'Top', right: 'Right', bottom: 'Bottom', left: 'Left' } as const;

type SpacingSide = keyof typeof SPACING_SIDE_LABELS;

/**
 * Builds the four-sided `top`/`right`/`bottom`/`left` custom fields shared by every CMS
 * component with a `padding` prop (Text/internal `Paragraph`, Heading, Button).
 *
 * Centralised rather than duplicated per-component because a `custom` field missing
 * `ai.schema` doesn't just make that one field un-editable by AI — Puck's AI plugin 400s
 * on the *entire* request the moment it reaches this field, breaking the whole chat for
 * that component. Hand-copying these four fields into each component risked exactly that
 * (see the `paragraph`/`heading`/`button` git history before this helper existed).
 */
export function createSpacingFields({ max }: { max?: number } = {}): Record<SpacingSide, CustomField<string>> {
  const fields = {} as Record<SpacingSide, CustomField<string>>;

  (Object.keys(SPACING_SIDE_LABELS) as SpacingSide[]).forEach((side) => {
    const label = SPACING_SIDE_LABELS[side];
    fields[side] = {
      type: 'custom',
      label,
      render: NumberField('px', undefined, max),
      ai: {
        schema: (PX_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions: `${label} padding, e.g. 8px.${max === undefined ? '' : ` Use 0 to ${max}px.`}`,
      },
    };
  });

  return fields;
}

export type SpacingPrefix = 'margin' | 'mobilePadding' | 'padding';
export type FlatSpacingProps<Prefix extends SpacingPrefix> = Record<`${Prefix}${Capitalize<SpacingSide>}`, string>;

/** Same side widgets as object spacing, with independently saved top-level keys. */
export function createFlatSpacingFields<Prefix extends SpacingPrefix>(
  prefix: Prefix,
  { max, labelPrefix }: { max?: number; labelPrefix?: string } = {}
): Record<keyof FlatSpacingProps<Prefix>, CustomField<string>> {
  const category = prefix === 'mobilePadding' ? 'mobile padding' : prefix;
  const fields = {} as Record<keyof FlatSpacingProps<Prefix>, CustomField<string>>;
  Object.values(SPACING_SIDE_LABELS).forEach((label) => {
    fields[`${prefix}${label}`] = {
      type: 'custom',
      label: labelPrefix ? `${labelPrefix} ${label.toLowerCase()}` : label,
      render: NumberField('px', undefined, max),
      ai: {
        schema: (PX_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions: `${label} ${category}, e.g. 8px.${max === undefined ? '' : ` Use 0 to ${max}px.`}`,
      },
    };
  });
  return fields;
}

/** An ephemeral CSS-formatting value, never a saved grouping object. */
export function readBoxSpacing<Prefix extends SpacingPrefix>(
  props: Partial<FlatSpacingProps<Prefix>>,
  prefix: Prefix
): Partial<BoxSpacing> {
  return Object.fromEntries(
    Object.entries(SPACING_SIDE_LABELS).map(([side, label]) => [side, props[`${prefix}${label}`]])
  );
}
